import React, { ReactNode } from 'react';
import {
  asyncWithLDProvider,
  camelCaseKeys,
  useLDClient
} from 'launchdarkly-react-client-sdk';

type LDProviderComponent = Awaited<ReturnType<typeof asyncWithLDProvider>>;
type LDClient = ReturnType<typeof useLDClient>;

let ldClient: LDClient;

/**
 * Reads a flag at the moment it is asked for, so modules built outside React — the
 * axios resources in `utilities/api`, created once at import time — can route by flag.
 *
 * `key` is the camel-cased name, the same form `useFlags()` exposes. `allFlags()`
 * returns LaunchDarkly's raw keys, which are not consistently cased, so they are
 * normalized here to keep one naming convention across the app.
 *
 * False until `CaptureLDClient` mounts, which is every feature's off state.
 * `createLDProvider` is awaited before first render, so the client is always in place
 * before a request is made.
 */
export const isFlagEnabled = (key: string): boolean =>
  ldClient ? camelCaseKeys(ldClient.allFlags())[key] === true : false;

/** Hands the client to `isFlagEnabled`. Render once, inside the LD provider. */
export const CaptureLDClient = ({ children }: { children: ReactNode }) => {
  const client = useLDClient();
  ldClient = client;

  return <>{children}</>;
};

/**
 * Placeholder context. LaunchDarkly targeting is unused in this app — per-org gating
 * compares flag *values* instead (see `helpers/featureFlagRenderItem`). `AppRouter`
 * still writes `userId` / `userEmail` here, but the client is created before render
 * and keeps its own copy, so those writes reach nothing.
 */
export const ldUser = {
  key: 'user_key',
  name: 'User name',
  email: 'user@email.com'
};

/** Longest first paint will wait on LaunchDarkly before giving up on it. */
const INIT_TIMEOUT_MS = 5000;

/** No LaunchDarkly context, so every `useFlags()` read is `{}` and features stay off. */
const FlagsUnavailableProvider = ({ children }: { children: ReactNode }) => (
  <>{children}</>
);

/**
 * Initializes LaunchDarkly and resolves once the flag values are known, so the first
 * render already sees them.
 *
 * `withLDProvider` initialized on mount instead, leaving every screen's first render
 * reading `{}`. For UI flags that is a flicker; for the flags that select a backend it
 * picked the wrong one — a screen fetched from Java, then refetched from Node when the
 * flag landed, and consumers that neither cancel nor sequence could let the slower Java
 * response overwrite the Node one.
 *
 * Never rejects, and never holds render past the timeout. `initLDClient` resolves
 * rather than throws when LaunchDarkly reports failure; the timeout covers a hang,
 * which would otherwise leave the page blank.
 */
export const createLDProvider = async (): Promise<LDProviderComponent> => {
  const clientSideID = "63f8ffa76c9ac213c946017a";
  if (!clientSideID) {
    return FlagsUnavailableProvider;
  }

  const timeout = new Promise<LDProviderComponent>(resolve => {
    setTimeout(() => resolve(FlagsUnavailableProvider), INIT_TIMEOUT_MS);
  });

  const initialize = asyncWithLDProvider({
    clientSideID,
    user: ldUser
  }).catch(() => FlagsUnavailableProvider);

  return Promise.race([initialize, timeout]);
};

export default createLDProvider;
